import type { EventThemePreset } from './types';

// Presentation only: `themePreset` picks colors, `kind` carries behavior and
// `alignment` carries layout. Any theme may be paired with any of them.
export const EVENT_THEME_PRESETS: Record<string, EventThemePreset> = {
    yellow: {
        bg: 'bg-yellow-400',
        bannerBorder: '',
        text: 'text-gray-900',
        accent: 'bg-gray-900',
        accentText: 'text-yellow-400',
        badgeBg: 'bg-gray-900',
        badgeText: 'text-yellow-400',
        subtitleText: 'text-gray-900',
        iconShadow: '0 4px 10px rgba(17,24,39,0.30)'
    },
    coral: {
        bg: 'bg-[#FDF1EC]',
        bannerBorder: 'border-b border-[#FADDD0]',
        text: 'text-gray-900',
        accent: 'bg-[#E96A3C]',
        accentText: 'text-white',
        badgeBg: 'bg-[#FADDD0]',
        badgeText: 'text-[#B3411E]',
        subtitleText: 'text-[#D2542B]',
        iconShadow: '0 4px 10px rgba(233,106,60,0.30)'
    },
    blue: {
        bg: 'bg-[#EFF6FF]',
        bannerBorder: 'border-b border-[#BFDBFE]',
        text: 'text-gray-900',
        accent: 'bg-blue-600',
        accentText: 'text-white',
        badgeBg: 'bg-[#DBEAFE]',
        badgeText: 'text-[#1D4ED8]',
        subtitleText: 'text-[#1D4ED8]',
        iconShadow: '0 4px 10px rgba(37,99,235,0.30)'
    },
    teal: {
        bg: 'bg-[#E6F4F5]',
        bannerBorder: 'border-b border-[#B8E0E3]',
        text: 'text-gray-900',
        accent: 'bg-[#007E87]',
        accentText: 'text-white',
        badgeBg: 'bg-[#CCEAEC]',
        badgeText: 'text-[#005F66]',
        subtitleText: 'text-[#00666E]',
        iconShadow: '0 4px 10px rgba(0,126,135,0.30)'
    },
    purple: {
        bg: 'bg-[#F5F3FF]',
        bannerBorder: 'border-b border-[#DDD6FE]',
        text: 'text-gray-900',
        accent: 'bg-[#7C3AED]',
        accentText: 'text-white',
        badgeBg: 'bg-[#EDE9FE]',
        badgeText: 'text-[#6D28D9]',
        subtitleText: 'text-[#6D28D9]',
        iconShadow: '0 4px 10px rgba(124,58,237,0.30)'
    },
    green: {
        bg: 'bg-[#F0FDF4]',
        bannerBorder: 'border-b border-[#BBF7D0]',
        text: 'text-gray-900',
        accent: 'bg-[#16A34A]',
        accentText: 'text-white',
        badgeBg: 'bg-[#DCFCE7]',
        badgeText: 'text-[#15803D]',
        subtitleText: 'text-[#15803D]',
        iconShadow: '0 4px 10px rgba(22,163,74,0.30)'
    },
    rose: {
        bg: 'bg-[#FFF1F2]',
        bannerBorder: 'border-b border-[#FECDD3]',
        text: 'text-gray-900',
        accent: 'bg-[#E11D48]',
        accentText: 'text-white',
        badgeBg: 'bg-[#FFE4E6]',
        badgeText: 'text-[#BE123C]',
        subtitleText: 'text-[#BE123C]',
        iconShadow: '0 4px 10px rgba(225,29,72,0.30)'
    },
    slate: {
        bg: 'bg-[#F8FAFC]',
        bannerBorder: 'border-b border-[#E2E8F0]',
        text: 'text-gray-900',
        accent: 'bg-[#334155]',
        accentText: 'text-white',
        badgeBg: 'bg-[#E2E8F0]',
        badgeText: 'text-[#334155]',
        subtitleText: 'text-[#475569]',
        iconShadow: '0 4px 10px rgba(51,65,85,0.30)'
    },
    amber: {
        bg: 'bg-[#FFFBEB]',
        bannerBorder: 'border-b border-[#FDE68A]',
        text: 'text-gray-900',
        accent: 'bg-[#D97706]',
        accentText: 'text-white',
        badgeBg: 'bg-[#FEF3C7]',
        badgeText: 'text-[#B45309]',
        subtitleText: 'text-[#B45309]',
        iconShadow: '0 4px 10px rgba(217,119,6,0.30)'
    },
    orange: {
        bg: 'bg-[#FFF7ED]',
        bannerBorder: 'border-b border-[#FED7AA]',
        text: 'text-gray-900',
        accent: 'bg-[#EA580C]',
        accentText: 'text-white',
        badgeBg: 'bg-[#FFEDD5]',
        badgeText: 'text-[#C2410C]',
        subtitleText: 'text-[#C2410C]',
        iconShadow: '0 4px 10px rgba(234,88,12,0.30)'
    },
    lime: {
        bg: 'bg-[#F7FEE7]',
        bannerBorder: 'border-b border-[#D9F99D]',
        text: 'text-gray-900',
        accent: 'bg-[#65A30D]',
        accentText: 'text-white',
        badgeBg: 'bg-[#ECFCCB]',
        badgeText: 'text-[#4D7C0F]',
        subtitleText: 'text-[#4D7C0F]',
        iconShadow: '0 4px 10px rgba(101,163,13,0.30)'
    },
    emerald: {
        bg: 'bg-[#ECFDF5]',
        bannerBorder: 'border-b border-[#A7F3D0]',
        text: 'text-gray-900',
        accent: 'bg-[#059669]',
        accentText: 'text-white',
        badgeBg: 'bg-[#D1FAE5]',
        badgeText: 'text-[#047857]',
        subtitleText: 'text-[#047857]',
        iconShadow: '0 4px 10px rgba(5,150,105,0.30)'
    },
    cyan: {
        bg: 'bg-[#ECFEFF]',
        bannerBorder: 'border-b border-[#A5F3FC]',
        text: 'text-gray-900',
        accent: 'bg-[#0891B2]',
        accentText: 'text-white',
        badgeBg: 'bg-[#CFFAFE]',
        badgeText: 'text-[#0E7490]',
        subtitleText: 'text-[#0E7490]',
        iconShadow: '0 4px 10px rgba(8,145,178,0.30)'
    },
    indigo: {
        bg: 'bg-[#EEF2FF]',
        bannerBorder: 'border-b border-[#C7D2FE]',
        text: 'text-gray-900',
        accent: 'bg-[#4F46E5]',
        accentText: 'text-white',
        badgeBg: 'bg-[#E0E7FF]',
        badgeText: 'text-[#4338CA]',
        subtitleText: 'text-[#4338CA]',
        iconShadow: '0 4px 10px rgba(79,70,229,0.30)'
    },
    fuchsia: {
        bg: 'bg-[#FDF4FF]',
        bannerBorder: 'border-b border-[#F5D0FE]',
        text: 'text-gray-900',
        accent: 'bg-[#C026D3]',
        accentText: 'text-white',
        badgeBg: 'bg-[#FAE8FF]',
        badgeText: 'text-[#A21CAF]',
        subtitleText: 'text-[#A21CAF]',
        iconShadow: '0 4px 10px rgba(192,38,211,0.30)'
    },
    pink: {
        bg: 'bg-[#FDF2F8]',
        bannerBorder: 'border-b border-[#FBCFE8]',
        text: 'text-gray-900',
        accent: 'bg-[#DB2777]',
        accentText: 'text-white',
        badgeBg: 'bg-[#FCE7F3]',
        badgeText: 'text-[#BE185D]',
        subtitleText: 'text-[#BE185D]',
        iconShadow: '0 4px 10px rgba(219,39,119,0.30)'
    },
    crimson: {
        bg: 'bg-[#FEF2F2]',
        bannerBorder: 'border-b border-[#FECACA]',
        text: 'text-gray-900',
        accent: 'bg-[#DC2626]',
        accentText: 'text-white',
        badgeBg: 'bg-[#FEE2E2]',
        badgeText: 'text-[#B91C1C]',
        subtitleText: 'text-[#B91C1C]',
        iconShadow: '0 4px 10px rgba(220,38,38,0.30)'
    },
    sand: {
        bg: 'bg-[#FAFAF9]',
        bannerBorder: 'border-b border-[#E7E5E4]',
        text: 'text-gray-900',
        accent: 'bg-[#78716C]',
        accentText: 'text-white',
        badgeBg: 'bg-[#F5F5F4]',
        badgeText: 'text-[#57534E]',
        subtitleText: 'text-[#57534E]',
        iconShadow: '0 4px 10px rgba(120,113,108,0.30)'
    },
    gold: {
        bg: 'bg-[#FEFCE8]',
        bannerBorder: 'border-b border-[#FEF08A]',
        text: 'text-gray-900',
        accent: 'bg-[#CA8A04]',
        accentText: 'text-white',
        badgeBg: 'bg-[#FEF9C3]',
        badgeText: 'text-[#A16207]',
        subtitleText: 'text-[#A16207]',
        iconShadow: '0 4px 10px rgba(202,138,4,0.30)'
    },
    navy: {
        bg: 'bg-[#EFF3FF]',
        bannerBorder: 'border-b border-[#C7D7FE]',
        text: 'text-gray-900',
        accent: 'bg-[#1E3A8A]',
        accentText: 'text-white',
        badgeBg: 'bg-[#DBEAFE]',
        badgeText: 'text-[#1E3A8A]',
        subtitleText: 'text-[#1E40AF]',
        iconShadow: '0 4px 10px rgba(30,58,138,0.30)'
    }
};

// Human-facing label and a swatch color for the admin picker. Keyed to
// EVENT_THEME_PRESETS; a key without an entry falls back to the raw key.
export const EVENT_THEME_PRESET_META: Record<string, { label: string; swatch: string }> = {
    yellow: { label: 'Sunrise Yellow', swatch: '#FACC15' },
    coral: { label: 'Coral', swatch: '#E96A3C' },
    blue: { label: 'Sky Blue', swatch: '#2563EB' },
    teal: { label: 'ODO Teal', swatch: '#007E87' },
    purple: { label: 'Violet', swatch: '#7C3AED' },
    green: { label: 'Fresh Green', swatch: '#16A34A' },
    rose: { label: 'Rose', swatch: '#E11D48' },
    slate: { label: 'Slate', swatch: '#334155' },
    amber: { label: 'Amber', swatch: '#D97706' },
    orange: { label: 'Sunset Orange', swatch: '#EA580C' },
    lime: { label: 'Lime', swatch: '#65A30D' },
    emerald: { label: 'Emerald', swatch: '#059669' },
    cyan: { label: 'Cyan', swatch: '#0891B2' },
    indigo: { label: 'Indigo', swatch: '#4F46E5' },
    fuchsia: { label: 'Fuchsia', swatch: '#C026D3' },
    pink: { label: 'Blossom Pink', swatch: '#DB2777' },
    crimson: { label: 'Crimson', swatch: '#DC2626' },
    sand: { label: 'Sand', swatch: '#78716C' },
    gold: { label: 'Gold', swatch: '#CA8A04' },
    navy: { label: 'Navy', swatch: '#1E3A8A' }
};

export function eventThemePresetLabel(key: string): string {
    return EVENT_THEME_PRESET_META[key]?.label ?? key;
}

export function eventThemePresetSwatch(key: string): string {
    return EVENT_THEME_PRESET_META[key]?.swatch ?? '#9CA3AF';
}

// An unknown/removed key must never crash the banner.
const DEFAULT_THEME_PRESET = EVENT_THEME_PRESETS.yellow;

export function resolveThemePreset(key: string | null | undefined): EventThemePreset {
    if (key && key in EVENT_THEME_PRESETS) {
        return EVENT_THEME_PRESETS[key];
    }
    return DEFAULT_THEME_PRESET;
}
